<script setup lang="ts">
import { computed } from "vue";

type CheckboxState = "checked" | "unchecked" | "indeterminate";

const props = withDefaults(
  defineProps<{ checked?: boolean; indeterminate?: boolean; disabled?: boolean }>(),
  { checked: false, indeterminate: false, disabled: false },
);

const emit = defineEmits<{ change: [checked: boolean] }>();

const state = computed<CheckboxState>(() =>
  props.indeterminate ? "indeterminate" : props.checked ? "checked" : "unchecked",
);

const handleClick = () => {
  if (!props.disabled) {
    emit("change", !props.checked);
  }
};
</script>

<template>
  <button
    type="button"
    class="checkbox"
    :class="[`checkbox-${state}`, { 'checkbox-disabled': disabled }]"
    :aria-checked="state === 'indeterminate' ? 'mixed' : checked"
    :disabled
    role="checkbox"
    @click.stop="handleClick"
  >
    <svg
      v-if="state === 'checked'"
      class="checkbox-icon"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 3L4.5 8.5L2 6"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <svg
      v-else-if="state === 'indeterminate'"
      class="checkbox-icon"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M2 6H10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
    </svg>
  </button>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background-color: colors.$background-ui;
  border: 1px solid colors.$border;
  border-radius: 3px;
  cursor: pointer;
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid colors.$main;
    outline-offset: 2px;
  }

  .checkbox-icon {
    flex-shrink: 0;
    pointer-events: none;
  }

  &-checked,
  &-indeterminate {
    background-color: colors.$main;
    border-color: colors.$main;

    .checkbox-icon {
      color: colors.$background-ui;
    }
  }

  &-disabled {
    cursor: not-allowed;
    background-color: colors.$background-disabled;
    border-color: colors.$border;

    &.checkbox-checked,
    &.checkbox-indeterminate {
      background-color: colors.$background-disabled;
      border-color: colors.$border;

      .checkbox-icon {
        color: colors.$text-disabled;
      }
    }
  }
}
</style>
